<template>
    <div>
        <Head title="Plans de maintenance" />

        <!-- Toolbar -->
        <div class="flex flex-col md:flex-row gap-3 mb-4 justify-between items-center">
            <search-input v-model="form.search" placeholder="Rechercher un plan..." class="w-full max-w-md" @reset="reset" />
            <div class="flex gap-2 items-center">
                <select-input v-model="form.active" class="w-40">
                    <option :value="null">Tous</option>
                    <option :value="1">Actifs</option>
                    <option :value="0">Inactifs</option>
                </select-input>
                <button class="btn-indigo" @click="openCreate">+ Nouveau plan</button>
            </div>
        </div>

        <!-- Table -->
        <div class="bg-white rounded-md shadow overflow-x-auto">
            <table class="min-w-full whitespace-nowrap">
                <tr class="text-left font-bold">
                    <th class="px-6 py-4">Titre</th>
                    <th class="px-6 py-4">Fréquence</th>
                    <th class="px-6 py-4">Bâtiment / Équipement</th>
                    <th class="px-6 py-4">Département</th>
                    <th class="px-6 py-4">Assigné à</th>
                    <th class="px-6 py-4">Prochain</th>
                    <th class="px-6 py-4">Dernier</th>
                    <th class="px-6 py-4">Statut</th>
                    <th class="px-6 py-4">Actions</th>
                </tr>
                <tr v-for="p in plans.data" :key="p.id" class="border-t hover:bg-gray-100" :class="p.is_overdue ? 'bg-red-50' : ''">
                    <td class="px-6 py-3 font-medium">{{ p.title }}</td>
                    <td class="px-6 py-3">
                        <span class="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">{{ freqLabel(p.frequency) }}</span>
                    </td>
                    <td class="px-6 py-3 text-sm">
                        <div v-if="p.building">🏢 {{ p.building }}</div>
                        <div v-if="p.equipment" class="text-gray-500">⚙ {{ p.equipment }}</div>
                    </td>
                    <td class="px-6 py-3 text-sm">{{ p.department || '—' }}</td>
                    <td class="px-6 py-3 text-sm">{{ p.assigned_to || '—' }}</td>
                    <td class="px-6 py-3 text-sm" :class="p.is_overdue ? 'text-red-600 font-bold' : p.is_due ? 'text-orange-600 font-semibold' : ''">
                        {{ p.next_run_at || '—' }}
                        <span v-if="p.is_overdue" class="ml-1 text-xs bg-red-100 text-red-600 px-1 rounded">En retard</span>
                        <span v-else-if="p.is_due" class="ml-1 text-xs bg-orange-100 text-orange-600 px-1 rounded">Échu</span>
                    </td>
                    <td class="px-6 py-3 text-sm text-gray-500">{{ p.last_run_at || '—' }}</td>
                    <td class="px-6 py-3">
                        <span :class="p.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'" class="px-2 py-0.5 rounded-full text-xs font-semibold">
                            {{ p.active ? 'Actif' : 'Inactif' }}
                        </span>
                    </td>
                    <td class="px-6 py-3 flex gap-2">
                        <button v-if="p.active" class="text-green-600 hover:underline text-sm" @click="execute(p.id)" title="Exécuter maintenant">▶</button>
                        <button class="text-indigo-600 hover:underline text-sm" @click="openEdit(p)">Modifier</button>
                        <button class="text-red-500 hover:underline text-sm" @click="destroy(p.id)">Supprimer</button>
                    </td>
                </tr>
                <tr v-if="plans.data.length === 0">
                    <td class="px-6 py-4 border-t" colspan="9">Aucun plan de maintenance trouvé.</td>
                </tr>
            </table>
        </div>
        <pagination class="mt-4" :links="plans.links" />

        <!-- Modal -->
        <div v-if="modal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-screen overflow-y-auto">
                <h2 class="text-lg font-bold mb-4">{{ editing ? 'Modifier le plan' : 'Nouveau plan de maintenance' }}</h2>
                <form @submit.prevent="submit">
                    <div class="grid grid-cols-2 gap-3">
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Titre *</label>
                            <input v-model="form2.title" type="text" class="form-input w-full" required maxlength="200" />
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Fréquence *</label>
                            <select v-model="form2.frequency" class="form-input w-full" required>
                                <option value="hebdomadaire">Hebdomadaire</option>
                                <option value="mensuel">Mensuel</option>
                                <option value="trimestriel">Trimestriel</option>
                                <option value="semestriel">Semestriel</option>
                                <option value="annuel">Annuel</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Bâtiment</label>
                            <select v-model="form2.building_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="b in buildings" :key="b.id" :value="b.id">{{ b.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Équipement</label>
                            <select v-model="form2.equipment_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="e in equipment" :key="e.id" :value="e.id">{{ e.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Département</label>
                            <select v-model="form2.department_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Catégorie</label>
                            <select v-model="form2.category_id" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="c in categories" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Assigné à</label>
                            <select v-model="form2.assigned_to" class="form-input w-full">
                                <option :value="null">—</option>
                                <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-sm font-medium mb-1">Prochaine exécution</label>
                            <input v-model="form2.next_run_at" type="date" class="form-input w-full" />
                        </div>
                        <div class="flex items-center gap-4 col-span-2 mt-1">
                            <label class="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" v-model="form2.auto_create" class="rounded" />
                                <span class="text-sm">Créer ticket automatiquement (CRON)</span>
                            </label>
                            <label class="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" v-model="form2.active" class="rounded" />
                                <span class="text-sm">Plan actif</span>
                            </label>
                        </div>
                        <div class="col-span-2">
                            <label class="block text-sm font-medium mb-1">Description</label>
                            <textarea v-model="form2.description" class="form-input w-full" rows="3"></textarea>
                        </div>
                    </div>
                    <div class="flex justify-end gap-2 mt-4">
                        <button type="button" class="btn-white" @click="modal = false">Annuler</button>
                        <button type="submit" class="btn-indigo" :disabled="submitting">
                            {{ submitting ? 'Enregistrement...' : 'Enregistrer' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SearchInput from '@/Shared/SearchInput'
import SelectInput from '@/Shared/SelectInput'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import throttle from 'lodash/throttle'

export default {
    layout: Layout,
    components: { Link, Head, Pagination, SearchInput, SelectInput },
    props: {
        plans: Object,
        filters: Object,
        buildings: Array,
        equipment: Array,
        departments: Array,
        categories: Array,
        users: Array,
    },
    remember: 'form',
    data() {
        return {
            form: {
                search: this.filters.search,
                active: this.filters.active ?? null,
            },
            modal: false,
            editing: null,
            submitting: false,
            form2: this.emptyForm(),
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                this.$inertia.get(this.route('maintenance.index'), pickBy(this.form), { replace: true, preserveState: true })
            }, 150),
        },
    },
    methods: {
        emptyForm() {
            return { title: '', description: '', equipment_id: null, building_id: null, category_id: null, department_id: null, assigned_to: null, frequency: 'mensuel', next_run_at: '', auto_create: true, active: true }
        },
        freqLabel(f) {
            const labels = { hebdomadaire: 'Hebdo', mensuel: 'Mensuel', trimestriel: 'Trimestriel', semestriel: 'Semestriel', annuel: 'Annuel' }
            return labels[f] || f
        },
        openCreate() {
            this.editing = null
            this.form2 = this.emptyForm()
            this.modal = true
        },
        openEdit(p) {
            this.editing = p.id
            this.form2 = {
                title: p.title, description: p.description || '', equipment_id: p.equipment_id ?? null,
                building_id: p.building_id ?? null, category_id: p.category_id ?? null,
                department_id: p.department_id ?? null, assigned_to: p.assigned_to ?? null,
                frequency: p.frequency, next_run_at: p.next_run_at || '',
                auto_create: p.auto_create, active: p.active,
            }
            this.modal = true
        },
        submit() {
            this.submitting = true
            if (this.editing) {
                this.$inertia.put(this.route('maintenance.update', this.editing), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            } else {
                this.$inertia.post(this.route('maintenance.store'), this.form2, {
                    onFinish: () => { this.submitting = false; this.modal = false },
                })
            }
        },
        execute(id) {
            if (confirm('Exécuter ce plan maintenant ? Un ticket de maintenance sera créé.')) {
                this.$inertia.post(this.route('maintenance.execute', id))
            }
        },
        destroy(id) {
            if (confirm('Supprimer ce plan de maintenance ?')) {
                this.$inertia.delete(this.route('maintenance.destroy', id))
            }
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
